import { DialogActions } from "@mui/material";
import React, { FunctionComponent, useEffect, useState } from "react";
import { SoecoButton } from "../SoecoButton";
import styled from "styled-components";
import QrScanner from "qr-scanner";
import { useNavigate } from "react-router-dom";
import { useGetQRCodeByUrl, useCreateQRCode } from "../../hooks/qrCodeHooks";
import {
    useGetProduct,
    getProduct,
    useAddProduct,
    useUpdateProduct,
} from "../../hooks/productHooks";
import { useQueryClient } from "@tanstack/react-query";
import { Product } from "../../types";
import { useSnackBar } from "../../context/SnackbarContext";

interface ScanQRCodeModalProps {
    onConfirm: () => void;
    onCancel: () => void;
    product?: Product | null;
    alreadyConnected?: number;
    navigateOnFound?: boolean;
}

export const ScanQRCodeModal: FunctionComponent<ScanQRCodeModalProps> = props => {
    const navigate = useNavigate();
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const lastScannedRef = React.useRef<string | null>(null);
    const isAttachingRef = React.useRef(false);
    const pendingNavigationRef = React.useRef<string | null>(null);

    const availableToConnect =
        (props.product ? props.product.quantity : 0) - (props.alreadyConnected || 0);

    const [scanResult, setScanResult] = useState<string | null>(null);
    const { data, isSuccess, isError } = useGetQRCodeByUrl(scanResult || "");
    const [qty, setQty] = useState<number>(availableToConnect);
    const [step, setStep] = useState<"quantity" | "scanner">(
        props.product && availableToConnect > 0 ? "quantity" : "scanner",
    );
    const [scannedCount, setScannedCount] = useState<number>(0);
    const [currentProduct, setCurrentProduct] = useState<Product | null>(props.product ?? null);
    const [occupiedBy, setOccupiedBy] = useState<{
        companyId: string;
        inventoryId: string;
        productId: string;
    } | null>(null);
    const { data: occupiedProduct } = useGetProduct(
        occupiedBy?.companyId ?? "",
        occupiedBy?.inventoryId ?? "",
        occupiedBy?.productId ?? "",
    );
    const [navigationError, setNavigationError] = useState<string | null>(null);

    const queryClient = useQueryClient();
    const { mutateAsync: createQRCode } = useCreateQRCode();
    const { mutateAsync: addProduct } = useAddProduct();
    const { mutateAsync: updateProduct } = useUpdateProduct();
    const { showSnackBar } = useSnackBar();

    const resetScannerForNext = () => {
        setScanResult(null);
        lastScannedRef.current = null;
        setOccupiedBy(null);
    };

    const handleConnectProductToQR = async (
        product: Product,
        qrUrl: string,
        currentCount: number,
        totalCount: number,
    ) => {
        if (isAttachingRef.current) return;
        isAttachingRef.current = true;

        const needsSplit = product.quantity > 1;

        try {
            let targetProductId = product.id!;

            if (needsSplit) {
                const newProduct = await addProduct({
                    companyId: product.companyId,
                    inventoryId: product.inventoryId,
                    product: { ...product, id: crypto.randomUUID(), quantity: 1 },
                    images: [],
                });
                targetProductId = newProduct.id!;

                const updatedQuantity = product.quantity - 1;
                await updateProduct({
                    companyId: product.companyId,
                    inventoryId: product.inventoryId,
                    productId: product.id!,
                    product: {
                        ...product,
                        quantity: updatedQuantity,
                        netPrice: product.netPrice ?? "",
                        purchasePrice: product.purchasePrice ?? "",
                        marketPrice: product.marketPrice ?? "",
                    },
                });
                setCurrentProduct({ ...product, quantity: updatedQuantity });
            }

            await createQRCode({
                qrCode: {
                    productId: targetProductId,
                    url: qrUrl,
                    qty: 1,
                    companyId: product.companyId,
                    inventoryId: product.inventoryId,
                },
            });

            const newCount = currentCount + 1;
            setScannedCount(newCount);

            if (newCount >= totalCount) {
                showSnackBar(
                    totalCount > 1
                        ? `All ${totalCount} QR codes connected successfully`
                        : "QR code connected successfully",
                    "success",
                );
                props.onConfirm();
            } else {
                showSnackBar(`QR code ${newCount} of ${totalCount} connected`, "success");
                resetScannerForNext();
            }
        } catch {
            showSnackBar("Failed to connect QR code", "error");
            resetScannerForNext();
        } finally {
            isAttachingRef.current = false;
        }
    };

    useEffect(() => {
        if (step !== "scanner") return;
        if (!videoRef.current) return;

        const scanner = new QrScanner(
            videoRef.current,
            (result: { data: string }) => {
                const newValue = result.data;
                if (lastScannedRef.current === newValue) return;
                lastScannedRef.current = newValue;
                setNavigationError(null);
                pendingNavigationRef.current = null;
                setScanResult(newValue);
            },
            {
                highlightScanRegion: true,
                highlightCodeOutline: true,
                preferredCamera: "environment",
            },
        );

        void scanner.start();

        return () => {
            scanner.stop();
            scanner.destroy();
        };
    }, [step]);

    useEffect(() => {
        if (!scanResult) return;

        if (props.navigateOnFound) {
            if (isSuccess && data) {
                setNavigationError(null);
                const token = data.productId;
                pendingNavigationRef.current = token;
                void queryClient
                    .fetchQuery({
                        queryKey: ["product", data.productId],
                        queryFn: () => getProduct(data.companyId, data.inventoryId, data.productId),
                        staleTime: 0,
                    })
                    .then(() => {
                        if (pendingNavigationRef.current !== token) return;
                        navigate(
                            `/companies/${data.companyId}/inventories/${data.inventoryId}/products/${data.productId}`,
                        );
                    })
                    .catch(() => {
                        if (pendingNavigationRef.current !== token) return;
                        setNavigationError("This QR code belongs to another company or inventory");
                    });
            } else if (isError || (isSuccess && !data)) {
                setOccupiedBy(null);
                setNavigationError(null);
            }
        } else {
            if (isSuccess && data) {
                setOccupiedBy({
                    companyId: data.companyId,
                    inventoryId: data.inventoryId,
                    productId: data.productId,
                });
            } else if ((isError || (isSuccess && !data)) && currentProduct) {
                setOccupiedBy(null);
                setNavigationError(null);
                void handleConnectProductToQR(currentProduct, scanResult, scannedCount, qty);
            }
        }
    }, [isSuccess, isError, data, scanResult]); // eslint-disable-line

    return (
        <React.Fragment>
            <ScanQRCodeModalWrapper>
                {step === "quantity" && props.product && (
                    <>
                        <HelpText>
                            How many of &quot;{props.product.name}&quot; do you want to connect to
                            QR-code?
                        </HelpText>
                        <input
                            type="number"
                            value={qty}
                            onChange={e => setQty(Number(e.target.value))}
                            min={1}
                            max={availableToConnect}
                        />
                    </>
                )}

                {step === "scanner" && (
                    <>
                        <video
                            ref={videoRef}
                            style={{ width: "100%", maxWidth: "400px", borderRadius: "8px" }}
                        />
                        {qty > 1 && (
                            <HelpText>
                                {scannedCount} / {qty} scanned
                            </HelpText>
                        )}
                        {navigationError ? (
                            <WarningText>{navigationError}</WarningText>
                        ) : occupiedBy ? (
                            <WarningText>
                                This QR code is already taken by{" "}
                                <strong>
                                    {occupiedProduct?.name ?? "another product (might be deleted)"}
                                </strong>
                            </WarningText>
                        ) : null}
                    </>
                )}

                <StyledActionsContainer>
                    <StyledDialogActions>
                        {props.product &&
                            availableToConnect > 0 &&
                            step === "scanner" &&
                            scannedCount === 0 && (
                                <SoecoButton onClick={() => setStep("quantity")}>Back</SoecoButton>
                            )}
                        <SoecoButton onClick={props.onCancel}>Cancel</SoecoButton>
                        {props.product && availableToConnect > 0 && step === "quantity" && (
                            <SoecoButton
                                onClick={() => setStep("scanner")}
                                disabled={qty === 0 || qty > availableToConnect}
                            >
                                NEXT
                            </SoecoButton>
                        )}
                    </StyledDialogActions>
                </StyledActionsContainer>
            </ScanQRCodeModalWrapper>
        </React.Fragment>
    );
};

const ScanQRCodeModalWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 1rem;
`;

const StyledActionsContainer = styled.div`
    width: 100%;
`;

const StyledDialogActions = styled(DialogActions)`
    display: flex;
    width: 100%;
`;

const HelpText = styled.p`
    font-size: 16;
    text-align: center;
    margin-top: -10px;
`;

const WarningText = styled.p`
    font-size: 16;
    text-align: center;
    margin-top: -10px;
    color: #d32f2f;
`;
